import React, { useEffect, useState } from "react";
import { Button, DatePicker, Select, Table, Tag, Tooltip } from "antd";
import { EyeFilled } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { post } from "../../../services/services";
import { ApiURL } from "../../../services/apiConstants";

const { RangePicker } = DatePicker;

const ENTRY_TYPE_COLOR = {
  work: "blue",
  rest: "green",
  break: "orange",
  meal: "purple",
  overtime: "red",
};

const RestHoursLogListCom = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [records, setRecords] = useState([]);
  const [ships, setShips] = useState([]);
  const [shipId, setShipId] = useState(null);
  const [staffList, setStaffList] = useState([]);
  const [staffId, setStaffId] = useState(null);
  const [entryType, setEntryType] = useState("");
  const [dates, setDates] = useState([dayjs().subtract(7, "day"), dayjs()]);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });

  const [startDate, endDate] = dates?.map((d) => d.format("YYYY-MM-DD")) || [];

  useEffect(() => {
    const FORM_DATA = new FormData();
    post(ApiURL.list_ships_view_com, FORM_DATA).then((res) => {
      if (res?.status === true) {
        const list = res.ships || [];
        setShips(list);
        if (list.length > 0) {
          setShipId(list[0].id);
          fetchStaff(list[0].id);
        }
      }
    });
  }, []);

  const fetchStaff = (sid) => {
    const FORM_DATA = new FormData();
    FORM_DATA.append("ship_id", sid);
    post(ApiURL.list_register_staff, FORM_DATA).then((res) => {
      if (res?.status === true) {
        const list = res.staff || [];
        setStaffList(list);
        if (list.length > 0) {
          setStaffId(list[0].staff_id);
        } else {
          setStaffId(null);
          setRecords([]);
        }
      }
    });
  };

  const fetchLogs = (page = 1, sid = staffId) => {
    if (!sid) return;
    setLoading(true);
    const FORM_DATA = new FormData();
    FORM_DATA.append("staff_id", sid);
    FORM_DATA.append("start_date", startDate || "");
    FORM_DATA.append("end_date", endDate || "");
    if (entryType) FORM_DATA.append("entry_type", entryType);
    FORM_DATA.append("page", page);
    FORM_DATA.append("page_size", pagination.pageSize);
    post(ApiURL.list_rest_hours_log_com, FORM_DATA).then((res) => {
      if (res?.status === true) {
        setRecords(res.data?.logs || res.data || []);
        if (res.pagination) {
          setPagination((p) => ({
            ...p,
            current: res.pagination.page,
            total: res.pagination.total,
          }));
        }
      } else {
        setRecords([]);
      }
      setLoading(false);
    });
  };

  const columns = [
    {
      title: "Id",
      dataIndex: "id",
      key: "id",
      align: "center",
      sorter: (a, b) => a.id - b.id,
    },
    {
      title: "Staff",
      dataIndex: "staff_name",
      key: "staff_name",
      align: "center",
      render: (v) => v || "—",
    },
    {
      title: "Date",
      dataIndex: "date",
      key: "date",
      align: "center",
    },
    {
      title: "Start Time",
      dataIndex: "start_time",
      key: "start_time",
      align: "center",
    },
    {
      title: "End Time",
      dataIndex: "end_time",
      key: "end_time",
      align: "center",
    },
    {
      title: "Duration (hrs)",
      dataIndex: "duration",
      key: "duration",
      align: "center",
      render: (v) => (v !== undefined ? Number(v).toFixed(1) : "—"),
    },
    {
      title: "Entry Type",
      dataIndex: "entry_type",
      key: "entry_type",
      align: "center",
      render: (t) => (
        <Tag color={ENTRY_TYPE_COLOR[t] || "default"} style={{ textTransform: "capitalize" }}>
          {t}
        </Tag>
      ),
    },
    {
      title: "Description",
      dataIndex: "description",
      key: "description",
      align: "center",
      render: (v) => v || "—",
    },
    {
      title: "Action",
      align: "center",
      key: "action",
      render: (_, record) => (
        <Tooltip title="View Monthly Record">
          <Button
            type="primary"
            size="small"
            icon={<EyeFilled />}
            onClick={() =>
              navigate("/resthours/crew-records", {
                state: {
                  staff_id: record.staff_id || staffId,
                  year: dayjs(record.date).year(),
                  month: dayjs(record.date).month() + 1,
                },
              })
            }
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <>
      {/* Filters */}
      <div className="row" style={{ marginBottom: 12, marginTop: 10 }}>
        <div className="col-3" style={{ marginLeft: 15 }}>
          <label className="block-label">Ship</label>
          <Select
            placeholder="Select Ship"
            value={shipId}
            style={{ width: "100%" }}
            onChange={(val) => {
              setShipId(val);
              setStaffId(null);
              setRecords([]);
              fetchStaff(val);
            }}
          >
            {ships.map((s) => (
              <Select.Option key={s.id} value={s.id}>
                {s.ship_name}
              </Select.Option>
            ))}
          </Select>
        </div>

        <div className="col-3">
          <label className="block-label">Crew Member</label>
          <Select
            placeholder="Select Staff"
            value={staffId}
            style={{ width: "100%" }}
            onChange={(val) => setStaffId(val)}
          >
            {staffList.map((s) => (
              <Select.Option key={s.staff_id} value={s.staff_id}>
                {s.first_name}
              </Select.Option>
            ))}
          </Select>
        </div>

        <div className="col-2">
          <label className="block-label">Entry Type</label>
          <Select
            placeholder="All Types"
            value={entryType || undefined}
            allowClear
            style={{ width: "100%" }}
            onChange={(val) => setEntryType(val || "")}
          >
            <Select.Option value="work">Work</Select.Option>
            <Select.Option value="rest">Rest</Select.Option>
            <Select.Option value="break">Break</Select.Option>
            <Select.Option value="meal">Meal</Select.Option>
            <Select.Option value="overtime">Overtime</Select.Option>
          </Select>
        </div>

        <div className="col-4" style={{ display: "flex", alignItems: "flex-end", gap: 8, paddingBottom: 1 }}>
          <div>
            <label className="block-label">Date Range</label>
            <RangePicker
              value={dates}
              onChange={(val) => {
                setDates(val);
                setPagination((p) => ({ ...p, current: 1 }));
              }}
              format="YYYY-MM-DD"
              style={{ width: 290 }}
              disabledDate={(c) => c && c > dayjs().endOf("day")}
            />
          </div>
          <Button
            type="primary"
            className="save_btn"
            style={{ marginBottom: 1 }}
            onClick={() => fetchLogs(1)}
          >
            Search
          </Button>
        </div>
      </div>

      <Table
        bordered
        columns={columns}
        dataSource={records}
        rowKey="id"
        loading={loading}
        className="custom-table"
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showTotal: (t) => `Total ${t} entries`,
          onChange: (page) => fetchLogs(page),
        }}
      />
    </>
  );
};

export default RestHoursLogListCom;
